<script setup lang="ts">
    import { computed } from 'vue';
    import FormGroup from '@/js/components/FormGroup.vue';
    import TextInput from '@/js/components/TextInput.vue';
    import CheckboxInput from '@/js/components/CheckboxInput.vue';
    import type { IntegrationConfigFieldDefinition } from '@/js/services/api/backOffice/types';

    const props = withDefaults(
        defineProps<{
            idPrefix: string;
            fields: IntegrationConfigFieldDefinition[];
            storedSecrets?: string[];
        }>(),
        {
            storedSecrets: () => [],
        }
    );

    const formValues = defineModel<Record<string, string>>({ required: true });

    // Fields with no section render together first, in declaration order; fields with a
    // section are grouped under it, in the order their section first appears.
    const sections = computed(() => {
        const bySection = new Map<string | null, IntegrationConfigFieldDefinition[]>();
        for (const field of props.fields) {
            const key = field.section;
            if (!bySection.has(key)) {
                bySection.set(key, []);
            }
            bySection.get(key)!.push(field);
        }
        return Array.from(bySection, ([section, fields]) => ({ section, fields }));
    });

    const multiCheckboxValues = (field: IntegrationConfigFieldDefinition) =>
        (formValues.value[field.key] ?? '').split(',').filter(Boolean);

    const toggleMultiCheckboxOption = (
        field: IntegrationConfigFieldDefinition,
        option: string,
        checked: boolean
    ) => {
        const current = multiCheckboxValues(field);
        const next = checked
            ? [...current.filter((v) => v !== option), option]
            : current.filter((v) => v !== option);
        formValues.value[field.key] = next.join(',');
    };
</script>
<template>
    <div
        v-for="group in sections"
        :key="group.section ?? '__ungrouped'"
        :class="group.section ? 'rounded-md border border-gray-light p-4' : ''"
    >
        <FormGroup>
            <template v-if="group.section" #header>{{ group.section }}</template>

            <template v-for="field in group.fields" :key="field.key">
                <div v-if="field.type === 'checkbox'">
                    <CheckboxInput
                        :id="`${idPrefix}-${field.key}`"
                        :model-value="formValues[field.key] === 'true'"
                        @update:model-value="formValues[field.key] = ($event ?? false).toString()"
                    >
                        {{ field.label }}
                    </CheckboxInput>
                </div>

                <div v-else-if="field.type === 'multi-checkbox'" class="flex flex-col gap-1">
                    <label class="text-overline">{{ field.label }}</label>
                    <CheckboxInput
                        v-for="option in field.options ?? []"
                        :id="`${idPrefix}-${field.key}-${option}`"
                        :key="option"
                        :model-value="multiCheckboxValues(field).includes(option)"
                        @update:model-value="
                            toggleMultiCheckboxOption(field, option, $event ?? false)
                        "
                    >
                        {{ option }}
                    </CheckboxInput>
                </div>

                <div v-else class="flex flex-col gap-1">
                    <label :for="`${idPrefix}-${field.key}`" class="text-overline">
                        {{ field.label }}
                        <span v-if="field.required" aria-hidden="true">*</span>
                    </label>
                    <TextInput
                        :id="`${idPrefix}-${field.key}`"
                        :model-value="formValues[field.key]"
                        :type="
                            field.type === 'password'
                                ? 'password'
                                : field.type === 'number'
                                  ? 'number'
                                  : 'text'
                        "
                        :placeholder="
                            field.type === 'password' && storedSecrets.includes(field.key)
                                ? '••••••••'
                                : ''
                        "
                        @update:model-value="
                            formValues[field.key] = $event == null ? '' : String($event)
                        "
                    />
                </div>
            </template>

            <slot :name="`section-${group.section ?? '__ungrouped'}`" />
        </FormGroup>
    </div>
</template>
